import { useEffect } from 'react'

import { Modal, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  JOB_SOURCE_CONNECTIONS_QUERY,
  UPDATE_INDEED_SOURCE_NAME,
} from 'src/graphql/jobSourceConnections.graphql'

type Props = {
  connection: {
    jobSourceConnectionId: string
    externalId: string
    jobSourceName?: string | null
  } | null
  opened: boolean
  onClose: () => void
}

/**
 * Admin-only editor for the Indeed partner `sourceName` Indeed approved for
 * this client. Jobs cannot be posted to the connection until it is set.
 */
const IndeedSourceNameModal = ({ connection, opened, onClose }: Props) => {
  const form = useForm({
    initialValues: { jobSourceName: '' },
    validateInputOnChange: true,
    validate: {
      jobSourceName: (value) =>
        value.trim().length === 0 ? 'Source name is required' : null,
    },
  })

  const [updateSourceName, { loading }] = useMutation(
    UPDATE_INDEED_SOURCE_NAME,
    {
      onCompleted: () => {
        toast('Indeed source name saved', 'success')
        onClose()
      },
      onError: (error) => {
        toast(error.message || 'Failed to save Indeed source name', 'error')
      },
      refetchQueries: [JOB_SOURCE_CONNECTIONS_QUERY],
    }
  )

  useEffect(() => {
    form.setValues({ jobSourceName: connection?.jobSourceName ?? '' })
  }, [connection, opened])

  const handleSubmit = ({ jobSourceName }: { jobSourceName: string }) => {
    if (!connection) return

    updateSourceName({
      variables: {
        jobSourceConnectionId: connection.jobSourceConnectionId,
        jobSourceName: jobSourceName.trim(),
      },
    })
  }

  const hasExistingName = Boolean(connection?.jobSourceName)

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Indeed source name"
      closeOnEscape
      closeOnClickOutside
      classNames={{ title: 'font-semibold' }}
    >
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        className="flex flex-col gap-4"
      >
        <div className="text-sm text-doubleNickel-gray-500">
          The source name Indeed approved for this client
          {connection ? ` (employer ID ${connection.externalId})` : ''}. Jobs
          cannot be posted to this account until it is set.
        </div>

        <TextInput
          label="Source name"
          placeholder="e.g. acme-trucking"
          required
          data-autofocus
          {...form.getInputProps('jobSourceName')}
        />

        {hasExistingName && (
          <div className="rounded-md bg-doubleNickel-warning-50 p-3 text-sm text-doubleNickel-warning-700">
            Indeed matches postings by source name. Changing it means the next
            sync creates new Indeed postings instead of updating the ones
            already live.
          </div>
        )}

        <div className="mt-2 flex justify-end gap-2">
          <DNButton variant="outline" onClick={onClose} type="button">
            Cancel
          </DNButton>
          <DNButton variant="primary" type="submit" loading={loading}>
            Save
          </DNButton>
        </div>
      </form>
    </Modal>
  )
}

export default IndeedSourceNameModal
